import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Firebase Auth drives Google sign-in via signInWithPopup.
 *
 * Redirect flow: we don't navigate inside the click handler because
 * `signInWithPopup` resolves before `onAuthStateChanged` finishes writing the
 * user to React state + Firestore. Instead we watch `user` from AuthContext —
 * the moment it becomes truthy (either via a fresh login or a refresh with an
 * existing session), we bounce to /dashboard. This also means visiting /login
 * while already signed in redirects automatically.
 */
export default function Login() {
  const { user, loading, signInWithGoogle } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
  const [busy, setBusy] = useState<'google' | null>(null);

  // Auto-redirect once Firebase confirms and the Firestore profile lands.
  useEffect(() => {
    if (!loading && user) {
      nav(from, { replace: true });
    }
  }, [user, loading, from, nav]);

  const handleGoogle = async () => {
    setBusy('google');
    try {
      await signInWithGoogle();
      toast.success('Signed in with Google');
      // Navigation happens in the useEffect above once user state flips.
    } catch (e: any) {
      toast.error(humanErr(e, 'Google'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] gradient-hero grid place-items-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="card p-8 animate-fade-in">
          <div className="flex items-center justify-center mb-6">
            <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white grid place-items-center shadow-pop">
              <Sparkles className="w-6 h-6" />
            </span>
          </div>
          <h1 className="text-2xl font-display font-bold text-center">Welcome to GradeLab</h1>
          <p className="text-center text-ink-500 mt-1">Sign in to evaluate, generate, and grade reports.</p>

          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={handleGoogle}
              disabled={busy !== null}
              className="btn-secondary w-full !rounded-full border border-ink-200 bg-white py-3 disabled:opacity-60"
            >
              {busy === 'google' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <path fill="#4285F4" d="M23 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.2a5.3 5.3 0 0 1-2.3 3.5v2.9h3.7c2.2-2 3.4-4.9 3.4-8.6z"/>
                  <path fill="#34A853" d="M12 23c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.4 1.1-3.9 1.1-3 0-5.6-2-6.5-4.7H1.7v2.9A11 11 0 0 0 12 23z"/>
                  <path fill="#FBBC05" d="M5.5 13.7a6.6 6.6 0 0 1 0-4.1V6.7H1.7a11 11 0 0 0 0 10.6l3.8-3z"/>
                  <path fill="#EA4335" d="M12 5.4c1.7 0 3.1.6 4.3 1.7l3.2-3.2A11 11 0 0 0 12 1C7.7 1 4 3.5 1.7 6.7l3.8 3C6.4 7.3 9 5.4 12 5.4z"/>
                </svg>
              )}
              Continue with Google
            </button>
          </div>

          <p className="mt-6 text-center text-xs text-ink-500">
            By continuing you agree to our <Link to="#" className="underline">Terms</Link> and <Link to="#" className="underline">Privacy Policy</Link>.
          </p>
        </div>
        <div className="text-center mt-4 text-sm text-ink-500">
          New here? <Link to="/pricing" className="text-brand-700 font-medium">See what you get</Link>.
        </div>
      </div>
    </div>
  );
}

function humanErr(err: any, provider: string): string {
  const code = err?.code ?? '';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in window was closed.';
  if (code === 'auth/cancelled-popup-request') return 'Previous sign-in is still running.';
  if (code === 'auth/popup-blocked') return 'Your browser blocked the popup. Allow popups and try again.';
  if (code === 'auth/operation-not-allowed') return `${provider} sign-in isn't enabled in Firebase yet.`;
  if (code === 'auth/unauthorized-domain') return 'This domain isn\'t authorized in Firebase Auth.';
  return err?.message ?? `${provider} sign-in failed`;
}
